import { OPTION_ALL_ROOMS } from '@xeetr/types/defaults'
import { TenantType } from '@xeetr/types/enums'
import { endOfDay, isValid, startOfDay } from 'date-fns'
import { Filter, MoreHorizontal, Search } from 'lucide-react'
import { useMemo, useState } from 'react'

import { useToolbarButtons } from '../../hooks/dashboard/useToolbarButtons'
import { ButtonDefinition } from '../../types/ButtonDefinition'
import TrackedButton from '../commons/TrackedButton'

import CalendarFooter from './common/CalendarFooter'

import { Button } from '@/components/ui/button'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BookingStateOption, getBookingStates } from '@/constants/bookingStates'
import { useDashboard } from '@/hooks/useDashboard'
import { useTenant } from '@/hooks/useTenant'
import { cn } from '@/lib/utils'
import { useRooms } from '@/hooks'

const Toolbar = () => {
  const { filterText, setFilterText, selectedRoom, setSelectedRoom } = useDashboard()
  const {
    selectedState,
    setSelectedState,
    selectedFrom,
    setSelectedFrom,
    selectedUntil,
    setSelectedUntil,
  } = useDashboard()
  const { tenant } = useTenant()
  const { rooms } = useRooms()

  const [showFilterBar, setShowFilterBar] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [showActions, setShowActions] = useState(false)

  const buttons = useToolbarButtons()

  const bookingStates = useMemo(() => getBookingStates(tenant?.type), [tenant?.type])

  const handleOnChangeDates = (range: { from?: Date; to?: Date | null }) => {
    if (range.from && range.to && isValid(range.from) && (range.to === null || isValid(range.to))) {
      setSelectedFrom(startOfDay(range.from))
      setSelectedUntil(endOfDay(range.to))
    }
  }

  const generateButton = (button: ButtonDefinition) => {
    return (
      <TrackedButton
        key={button.trackingkey}
        {...button}
        variant={button.variant || 'outline'}
        hidden={button.visible === false}
      />
    )
  }

  const roomsWithAllOption = useMemo(() => {
    if (!rooms) return []
    if (rooms.length > 1) return [OPTION_ALL_ROOMS, ...rooms]
    return rooms
  }, [rooms])

  const generateFilterDropdowns = (
    <>
      <Select
        value={selectedState}
        onValueChange={(value: BookingStateOption['value']) => setSelectedState(value)}
      >
        <SelectTrigger className="min-w-[16rem] max-w-[20rem]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {bookingStates.map((state) => (
            <SelectItem key={state.value} value={state.value}>
              {state.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {tenant?.type === TenantType.FOOD && roomsWithAllOption.length > 1 && (
        <Select value={selectedRoom} onValueChange={setSelectedRoom}>
          <SelectTrigger className="w-fit">
            <SelectValue placeholder="Raum auswählen" />
          </SelectTrigger>
          <SelectContent>
            {roomsWithAllOption.map((room) => (
              <SelectItem key={room.id} value={room.id}>
                {room.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </>
  )

  const generateSearchBar = (
    <div className="relative w-full min-w-[16rem] max-w-[20rem]">
      <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
      <Input
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        className="w-full pl-9"
        placeholder="Reservierungen durchsuchen"
      />
    </div>
  )

  return (
    <div className="bg-card grid grid-cols-10 gap-2 rounded-lg border p-4 shadow-sm lg:flex lg:flex-col lg:gap-3">
      <div className="toolbar:hidden col-span-6">
        <DateRangePicker
          from={selectedFrom}
          to={selectedUntil}
          onDateRangeChange={handleOnChangeDates}
          footer={<CalendarFooter />}
          showIcon
          showTime={tenant?.showTimeInDashboard}
          dateFormat="dd.MM.yy"
        />
      </div>

      <div className="col-span-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="max-toolbar:hidden flex gap-2">{generateFilterDropdowns}</div>
          <div className="toolbar:hidden flex items-center gap-2">
            <Button
              variant={showFilterBar ? 'default' : 'outline'}
              size="icon"
              onClick={() => setShowFilterBar(!showFilterBar)}
            >
              <Filter className="h-4 w-4" />
            </Button>
            <Button
              variant={showSearch ? 'default' : 'outline'}
              size="icon"
              onClick={() => setShowSearch(!showSearch)}
            >
              <Search className="h-4 w-4" />
            </Button>
            <Button
              variant={showActions ? 'default' : 'outline'}
              size="icon"
              onClick={() => setShowActions(!showActions)}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </div>
          <div className="max-toolbar:hidden">{generateSearchBar}</div>
        </div>
        <div className="max-toolbar:hidden flex flex-wrap items-center gap-2">
          <DateRangePicker
            from={selectedFrom}
            to={selectedUntil!}
            onDateRangeChange={handleOnChangeDates}
            footer={<CalendarFooter />}
            showIcon
            showTime={tenant?.showTimeInDashboard}
            dateFormat="dd.MM.yy"
          />
          {buttons.map(generateButton)}
        </div>
      </div>
      <div
        className={cn('toolbar:hidden flex flex-wrap gap-2', {
          hidden: !showFilterBar,
        })}
      >
        {generateFilterDropdowns}
      </div>
      <div
        className={cn('toolbar:hidden', {
          hidden: !showSearch,
        })}
      >
        {generateSearchBar}
      </div>
      <div
        className={cn('toolbar:hidden col-span-10 flex flex-wrap gap-2', {
          hidden: !showActions,
        })}
      >
        {buttons.map(generateButton)}
      </div>
    </div>
  )
}

export default Toolbar
